// Input validation utilities for security

export const validateEmail = (email: string): { isValid: boolean; error?: string } => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!email) {
    return { isValid: false, error: 'Email is required' };
  }
  
  if (email.length > 254) {
    return { isValid: false, error: 'Email is too long' };
  }
  
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Invalid email format' };
  }
  
  return { isValid: true };
};

export const validatePassword = (password: string): { isValid: boolean; error?: string } => {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }
  
  if (password.length < 8) {
    return { isValid: false, error: 'Password must be at least 8 characters long' };
  }
  
  if (password.length > 128) {
    return { isValid: false, error: 'Password is too long' };
  }
  
  // Check for at least one uppercase, one lowercase, and one number
  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one uppercase letter, one lowercase letter, and one number' };
  }
  
  return { isValid: true };
};

export const validatePromoCode = (code: string): { isValid: boolean; error?: string } => {
  if (!code) {
    return { isValid: false, error: 'Promo code is required' };
  }
  
  // Remove whitespace and convert to uppercase
  const cleanCode = code.trim().toUpperCase();
  
  if (cleanCode.length < 3 || cleanCode.length > 20) {
    return { isValid: false, error: 'Promo code must be between 3 and 20 characters' };
  }
  
  // Only allow alphanumeric characters, hyphens, and underscores
  if (!/^[A-Z0-9-_]+$/.test(cleanCode)) {
    return { isValid: false, error: 'Promo code can only contain letters, numbers, hyphens, and underscores' };
  }
  
  return { isValid: true };
};

export const sanitizeString = (input: string, maxLength = 255): string => {
  // Remove null bytes and control characters except tabs and newlines
  let sanitized = input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  
  // Trim whitespace
  sanitized = sanitized.trim();
  
  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  return sanitized;
};

/**
 * Same cleanup as sanitizeString, but preserves spaces the user is typing
 * (no trimming). Use for onChange handlers; trim on submit instead.
 */
export const sanitizeInputValue = (input: string, maxLength = 255): string => {
  let sanitized = input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  // Collapse leading whitespace only, keep inner/trailing spaces while typing
  sanitized = sanitized.replace(/^\s+/, '');
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  return sanitized;
};

export const validateCreditAmount = (amount: number): { isValid: boolean; error?: string } => {
  if (isNaN(amount)) {
    return { isValid: false, error: 'Invalid amount' };
  }
  
  if (amount === 0) {
    return { isValid: false, error: 'Amount cannot be zero' };
  }
  
  if (amount < -10000 || amount > 10000) {
    return { isValid: false, error: 'Amount must be between -$10,000 and $10,000' };
  }
  
  // Check for more than 2 decimal places
  if (Math.round(amount * 100) !== amount * 100) {
    return { isValid: false, error: 'Amount cannot have more than 2 decimal places' };
  }
  
  return { isValid: true };
};